import { useState } from "react";

interface PaginationProps {
  current: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function buildPages(current: number, total: number): (number | string)[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => index + 1);
  }
  const pages: (number | string)[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push("...");
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}

export default function Pagination({ current, totalPages, onChange }: PaginationProps) {
  const [jump, setJump] = useState("");

  if (totalPages <= 1) return null;

  const pages = buildPages(current, totalPages);

  const go = (page: number) => {
    if (page < 1 || page > totalPages || page === current) return;
    onChange(page);
  };

  const handleJump = () => {
    const parsed = Number.parseInt(jump, 10);
    if (!Number.isNaN(parsed)) {
      go(parsed);
      setJump("");
    }
  };

  return (
    <nav aria-label="分页导航" className="mt-8 flex flex-wrap items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => go(current - 1)}
        disabled={current === 1}
        className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-700"
      >
        <i className="ri-arrow-left-s-line" aria-hidden="true" />
        上一页
      </button>

      {pages.map((page, index) =>
        typeof page === "number" ? (
          <button
            key={page}
            type="button"
            onClick={() => go(page)}
            aria-current={page === current ? "page" : undefined}
            className={`flex h-9 w-9 items-center justify-center rounded-md border text-sm transition-colors ${
              page === current
                ? "border-primary-600 bg-primary-600 font-semibold text-background-50"
                : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
            }`}
          >
            {page}
          </button>
        ) : (
          <span key={`gap-${index}`} className="flex h-9 w-9 items-center justify-center text-foreground-400">
            …
          </span>
        )
      )}

      <button
        type="button"
        onClick={() => go(current + 1)}
        disabled={current === totalPages}
        className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-background-300 disabled:hover:text-foreground-700"
      >
        下一页
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
      </button>

      <span className="ml-2 flex items-center gap-2 text-sm text-foreground-600">
        跳至
        <input
          type="number"
          min={1}
          max={totalPages}
          value={jump}
          onChange={(event) => setJump(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleJump();
          }}
          aria-label="跳转到指定页"
          className="h-9 w-16 rounded-md border border-background-300 bg-background-50 px-2 text-center text-sm text-foreground-900 outline-none focus:border-primary-400"
        />
        页
        <button
          type="button"
          onClick={handleJump}
          className="h-9 rounded-md bg-secondary-500 px-3 text-sm text-background-50 transition-colors hover:bg-secondary-600"
        >
          跳转
        </button>
      </span>
    </nav>
  );
}